import React from "react";
import { toFixed } from "../CustomComponent";
import { blockStyle, cardStyle } from "./constants";

const HORIZONS = [6, 12, 24];

const Fact = ({ label, value, hint }) => (
  <div className="col-6 col-lg-3">
    <div className="rounded-4 p-3 h-100" style={cardStyle}>
      <div className="small text-secondary mb-1">{label}</div>
      <div className="fw-bold" style={{ color: "#0b4f84", fontSize: "clamp(0.95rem, 2vw, 1.2rem)" }}>{value}</div>
      {hint ? <div className="text-secondary" style={{ fontSize: "0.72rem" }}>{hint}</div> : null}
    </div>
  </div>
);

// พยากรณ์ภาษีเดือนข้างหน้า จากสัญญา MA + ฐานเงินเดือนปัจจุบัน + ค่าเฉลี่ยย้อนหลัง 3 เดือน
export default function ForecastPanel({ forecast, horizon, setHorizon }) {
  const months = forecast?.months || [];
  const totals = forecast?.totals || {};
  const assumptions = forecast?.assumptions || {};
  const maxTotal = months.reduce((max, item) => Math.max(max, item.total), 0) || 1;

  return (
    <>
      <div className="row g-3 mb-4">
        <Fact label="ภาษีที่ต้องเสียในช่วงพยากรณ์" value={`${toFixed(totals.total)} บาท`} hint={`${months.length} เดือนข้างหน้า`} />
        <Fact label="ภ.พ.30 (VAT)" value={toFixed(totals.pp30)} hint="ภาษีขาย - ภาษีซื้อ" />
        <Fact label="ภ.ง.ด.1 + หัก ณ ที่จ่าย" value={toFixed((totals.pgd1 || 0) + (totals.wht || 0))} hint="หักจากพนักงานและผู้รับจ้าง" />
        <Fact label="ประกันสังคม" value={toFixed(totals.sso)} hint="ลูกจ้าง + นายจ้าง" />
      </div>

      <div className="p-3 p-md-4 mb-4" style={blockStyle}>
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
          <h6 className="fw-semibold mb-0">ประมาณการภาษีรายเดือนล่วงหน้า</h6>
          <div className="btn-group">
            {HORIZONS.map((value) => (
              <button
                key={value}
                type="button"
                className={`btn btn-sm ${horizon === value ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setHorizon(value)}
              >
                {value} เดือน
              </button>
            ))}
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">เดือน</th>
                <th className="text-secondary fw-medium text-end text-nowrap">รายได้คาดการณ์</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ค่าใช้จ่ายคาดการณ์</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ภ.ง.ด.1</th>
                <th className="text-secondary fw-medium text-end text-nowrap">หัก ณ ที่จ่าย</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ภ.พ.30</th>
                <th className="text-secondary fw-medium text-end text-nowrap">สปส.</th>
                <th className="text-secondary fw-medium text-end text-nowrap">รวมภาษี</th>
              </tr>
            </thead>
            <tbody>
              {months.map((month) => (
                <tr key={month.period_month}>
                  <td className="fw-semibold text-nowrap" style={{ color: "#12314d" }}>
                    {month.label}
                    {month.ma_revenue > 0 ? (
                      <div className="small text-secondary fw-normal">มีบิล MA {toFixed(month.ma_revenue)}</div>
                    ) : null}
                  </td>
                  <td className="text-end">{toFixed(month.revenue)}</td>
                  <td className="text-end text-secondary">{toFixed(month.expense)}</td>
                  <td className="text-end">{toFixed(month.forms.pgd1)}</td>
                  <td className="text-end">{toFixed(month.forms.wht)}</td>
                  <td className="text-end">{toFixed(month.forms.pp30)}</td>
                  <td className="text-end">{toFixed(month.forms.sso)}</td>
                  <td className="text-end fw-bold" style={{ color: "#b63636" }}>
                    {toFixed(month.total)}
                    <div className="mt-1" style={{ height: 4, background: "#eef3f7", borderRadius: 4 }}>
                      <div style={{ width: `${(month.total / maxTotal) * 100}%`, height: 4, background: "#0c76e5", borderRadius: 4 }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                <td className="fw-bold">รวม</td>
                <td className="text-end fw-bold">{toFixed(totals.revenue)}</td>
                <td className="text-end fw-bold text-secondary">{toFixed(totals.expense)}</td>
                <td className="text-end fw-bold">{toFixed(totals.pgd1)}</td>
                <td className="text-end fw-bold">{toFixed(totals.wht)}</td>
                <td className="text-end fw-bold">{toFixed(totals.pp30)}</td>
                <td className="text-end fw-bold">{toFixed(totals.sso)}</td>
                <td className="text-end fw-bold" style={{ color: "#b63636" }}>{toFixed(totals.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="p-3 p-md-4 h-100" style={blockStyle}>
            <h6 className="fw-semibold mb-3">สมมติฐานที่ใช้พยากรณ์</h6>
            <ul className="mb-0 ps-3 small" style={{ color: "#12314d" }}>
              <li className="mb-1">
                รายได้ต่อเดือน = ค่าเฉลี่ย {assumptions.lookback_months || 3} เดือนล่าสุด ({toFixed(assumptions.avg_income)} บาท)
                เดือนไหนมีบิลตามสัญญา MA มากกว่า จะใช้ยอด MA แทน (มีสัญญาในระบบ {assumptions.ma_contract_count || 0} ฉบับ)
              </li>
              <li className="mb-1">ค่าใช้จ่ายต่อเดือน = ค่าเฉลี่ย 3 เดือนล่าสุด ({toFixed(assumptions.avg_expense)} บาท)</li>
              <li className="mb-1">
                ภาษีซื้อคิดจากสัดส่วนค่าใช้จ่ายที่มีใบกำกับภาษี {toFixed(assumptions.vat_share, 0)}% ·
                หัก ณ ที่จ่ายคิดจากสัดส่วนรายจ่ายที่ต้องหัก {toFixed(assumptions.wht_share, 0)}%
              </li>
              <li className="mb-1">
                ภ.ง.ด.1 และประกันสังคม คิดจากฐานเงินเดือนพนักงานปัจจุบัน {assumptions.employee_count || 0} คน
                (รวมเงินเดือน {toFixed(assumptions.payroll_base)} บาท/เดือน)
              </li>
              <li>ยังไม่รวมโบนัส ปรับเงินเดือน หรือรายจ่ายก้อนใหญ่ที่ยังไม่เคยเกิด</li>
            </ul>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="p-3 p-md-4 h-100" style={blockStyle}>
            <h6 className="fw-semibold mb-3">ภาษีนิติบุคคลที่คาดว่าจะเกิดในช่วงนี้</h6>
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-secondary">กำไรที่คาดว่าจะได้</span>
              <span className="fw-semibold">{toFixed(forecast?.projected_cit?.profit)}</span>
            </div>
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-secondary">อัตราที่ใช้</span>
              <span className="fw-semibold">{forecast?.projected_cit?.is_sme ? "ขั้นบันได SME" : "20% อัตราเดียว"}</span>
            </div>
            <div className="d-flex justify-content-between py-2">
              <span className="fw-semibold">ภาษีนิติบุคคลโดยประมาณ</span>
              <span className="fw-bold" style={{ color: "#b63636" }}>{toFixed(forecast?.projected_cit?.tax)}</span>
            </div>
            <div className="small text-secondary mt-2">
              ตัวเลขนี้ยังไม่ได้หักค่าใช้จ่ายทางภาษีที่บวกกลับ/หักเพิ่มได้ ใช้ดูแนวโน้มเพื่อกันเงินไว้ล่วงหน้า
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
